import React from 'react';
import PolicyPageLayout from './PolicyPageLayout';

const CookiePolicy = () => {
  const sections = [
    {
      title: "What Are Cookies?",
      content: [
        "Cookies are small text files stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, keeping you logged in, and understanding how you use our platform.",
        "This policy explains what cookies we use, why we use them, and how you can manage your cookie preferences."
      ]
    },
    {
      title: "Types of Cookies We Use",
      content: [
        "We use different types of cookies for various purposes. Here's a breakdown of the cookies active on our platform:"
      ]
    },
    {
      title: "Essential Cookies",
      content: [
        "These cookies are necessary for the website to function properly. They enable core functionality like security, account authentication, and session management. You cannot disable these cookies."
      ],
      bullets: [
        "session_id: Maintains your login session",
        "csrf_token: Protects against cross-site request forgery",
        "cookie_consent: Remembers your cookie preferences",
        "security_check: Validates secure connections"
      ]
    },
    {
      title: "Functional Cookies",
      content: [
        "These cookies enable enhanced functionality and personalization. They remember your preferences and settings to provide a more tailored experience."
      ],
      bullets: [
        "user_preferences: Stores your UI settings (theme, language)",
        "recent_projects: Remembers your recently accessed projects",
        "audio_settings: Saves your default audio processing preferences",
        "tour_completed: Tracks onboarding progress"
      ]
    },
    {
      title: "Analytics Cookies",
      content: [
        "These cookies help us understand how visitors interact with our website. They collect information about page views, time spent on pages, and navigation patterns. This data is aggregated and anonymized."
      ],
      bullets: [
        "_ga: Google Analytics identifier for usage tracking",
        "_gid: Google Analytics session tracking",
        "amplitude_id: Product analytics for feature usage",
        "mixpanel_token: User behavior analytics"
      ]
    },
    {
      title: "Marketing Cookies",
      content: [
        "These cookies track your online activity to help us deliver relevant advertising. They are set by our advertising partners and help measure campaign effectiveness. You can opt out of these cookies."
      ],
      bullets: [
        "fbp: Facebook pixel for ad targeting",
        "_gcl_au: Google Ads conversion tracking",
        "li_fat_id: LinkedIn ad attribution",
        "hubspotutk: HubSpot marketing tracking"
      ]
    },
    {
      title: "Third-Party Cookies",
      content: [
        "Some cookies are placed by third-party services that appear on our pages. These include embedded content, social media buttons, and integration widgets."
      ],
      bullets: [
        "YouTube: For embedded video tutorials",
        "Stripe: For secure payment processing",
        "Intercom: For customer support chat",
        "Calendly: For scheduling demos"
      ]
    },
    {
      title: "Managing Your Cookie Preferences",
      content: [
        "You can manage your cookie preferences at any time. Here's how:"
      ],
      bullets: [
        "Cookie Settings: Click 'Cookie Settings' in our footer to update preferences",
        "Browser Settings: Configure cookies in your browser's privacy settings",
        "Opt-Out Tools: Use industry opt-out tools like YourAdChoices or NAI",
        "Do Not Track: We respect browser 'Do Not Track' signals"
      ]
    },
    {
      title: "Cookie Retention",
      content: [
        "Different cookies are stored for different lengths of time:"
      ],
      bullets: [
        "Session cookies: Deleted when you close your browser",
        "Persistent cookies: Up to 2 years depending on purpose",
        "Analytics cookies: 26 months maximum",
        "Marketing cookies: 90 days to 13 months"
      ]
    }
  ];

  const faqs = [
    {
      question: "Can I use the platform without accepting cookies?",
      answer: "Essential cookies are required for the platform to function. However, you can decline functional, analytics, and marketing cookies and still use our core services with limited personalization."
    },
    {
      question: "How do I change my cookie preferences?",
      answer: "You can change your preferences at any time by clicking 'Cookie Settings' in the footer or navigating to Settings > Privacy in your account. Changes take effect immediately."
    },
    {
      question: "What happens if I block all cookies?",
      answer: "Blocking all cookies, including essential ones, may prevent you from logging in or using certain features. We recommend keeping essential cookies enabled for the best experience."
    },
    {
      question: "Do you sell data collected through cookies?",
      answer: "No, we never sell your personal data. Marketing cookies may share data with advertising partners for targeted ads, but this can be disabled in your cookie preferences."
    },
    {
      question: "How can I delete cookies already stored?",
      answer: "You can delete cookies through your browser settings. In most browsers, go to Settings > Privacy > Clear browsing data > Cookies. Note that this will log you out of most websites."
    },
    {
      question: "Are cookies the same as tracking pixels?",
      answer: "No, tracking pixels (web beacons) are different from cookies. They are tiny invisible images that track email opens and page views. You can disable images in emails to block email tracking pixels."
    }
  ];

  return (
    <PolicyPageLayout
      title="Cookie Policy"
      lastUpdated="February 10, 2026"
      sections={sections}
      faqs={faqs}
    />
  );
};

export default CookiePolicy;
